// ═══════════════════════════════════════════════════════════════════════════
// PlayerContext — global state for the persistent audio player that
// survives route changes. The audio element itself lives inside
// <PersistentPlayer />; this context is the source of truth for what
// to play, whether it's playing, whether the bar is minimized vs
// expanded, and whether it's open at all (X to close, profile click
// to re-open).
//
// Designed for the profile audio playback flow:
//   • Profile click on a creator's audio sample → playTrack({...})
//   • Bar appears at the bottom, expanded, playing
//   • User navigates anywhere — bar stays mounted, audio keeps going
//   • Tap the minimize chevron → bar collapses to a small floating tab
//   • Tap the tab → bar expands again
//   • Tap the X → audio stops, state cleared, bar disappears
//   • Click another profile's play → reopens with that track
//
// Queue support is scaffolded (queue + currentIndex) so a future PR
// can add multi-clip playlists (up to 120s of clips, per spec).
// ═══════════════════════════════════════════════════════════════════════════

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const Ctx = createContext(null);

const INITIAL = {
  // Single source of truth for "what's loaded right now"
  track:      null,    // { url, title, subtitle?, coverUrl?, creator?, source? }
  queue:      [],      // future: full playlist
  queueIndex: 0,
  // Playback intent. The actual <audio> element reads these and
  // play()/pause()s itself. We don't ref the element from here so the
  // context stays serializable + multi-instance safe.
  playing:    false,
  // UI state
  visible:    false,   // true = bar is in the DOM at all
  minimized:  false,   // true = collapsed to a small floating tab
};

export function PlayerProvider({ children }) {
  const [state, setState] = useState(INITIAL);

  // Open the player with a track. If the same track is already loaded,
  // just resumes; otherwise replaces.
  const playTrack = useCallback((track, opts = {}) => {
    if (!track?.url) return;
    setState(s => {
      const sameTrack = s.track?.url === track.url;
      // Locate this track within the provided queue so next/prev work.
      const queue = opts.queue && opts.queue.length ? opts.queue : [track];
      const idx = Math.max(0, queue.findIndex(t => t?.url === track.url));
      return {
        ...s,
        track:   sameTrack ? s.track : track,
        queue,
        queueIndex: idx,
        playing: true,
        visible: true,
        minimized: opts.minimized ?? false,
      };
    });
  }, []);

  // Open the player on a whole queue at a given index. Tracks may be "lazy"
  // (no url yet, just a `resolve()` the player calls when it lands on them) —
  // used by the home collections to preview through a playlist without
  // pre-generating every episode's audio.
  const playQueue = useCallback((queue, startIndex = 0) => {
    if (!Array.isArray(queue) || !queue.length) return;
    const idx = Math.max(0, Math.min(queue.length - 1, startIndex));
    setState(s => ({
      ...s,
      queue,
      queueIndex: idx,
      track: queue[idx],
      playing: true,
      visible: true,
      minimized: false,
    }));
  }, []);

  const pause   = useCallback(() => setState(s => ({ ...s, playing: false })), []);
  const resume  = useCallback(() => setState(s => (s.track ? { ...s, playing: true, visible: true } : s)), []);
  const toggle  = useCallback(() => setState(s => (s.track ? { ...s, playing: !s.playing } : s)), []);
  const minimize = useCallback(() => setState(s => ({ ...s, minimized: true })), []);
  const expand   = useCallback(() => setState(s => ({ ...s, minimized: false })), []);
  const close    = useCallback(() => setState(() => ({ ...INITIAL })), []);

  // Move through the queue. Clamped at the ends (no wrap) so "next" on the
  // last track is a no-op rather than looping.
  const step = useCallback((dir) => setState(s => {
    if (!s.queue || s.queue.length < 2) return s;
    const nextIndex = s.queueIndex + dir;
    if (nextIndex < 0 || nextIndex >= s.queue.length) return s;
    const nextTrack = s.queue[nextIndex];
    if (!nextTrack) return s;
    // Queue tracks are lazy — a url may not be resolved yet. Advance anyway;
    // PersistentPlayer's resolve effect fetches the src when it lands on the
    // track and playback starts once it lands. Bailing on a missing url here
    // is what stalled the collection preview playlist mid-advance.
    return { ...s, track: nextTrack, queueIndex: nextIndex, playing: true, visible: true };
  }), []);
  const next = useCallback(() => step(1), [step]);
  const prev = useCallback(() => step(-1), [step]);

  const value = useMemo(() => ({
    ...state,
    hasNext: state.queue.length > 1 && state.queueIndex < state.queue.length - 1,
    hasPrev: state.queue.length > 1 && state.queueIndex > 0,
    playTrack, playQueue, pause, resume, toggle, minimize, expand, close, next, prev,
  }), [state, playTrack, playQueue, pause, resume, toggle, minimize, expand, close, next, prev]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePlayer() {
  const ctx = useContext(Ctx);
  // Defensive — if somehow called outside the provider (older code
  // paths), return a no-op shape so renders don't crash. The UI just
  // won't open.
  if (!ctx) {
    return {
      track: null, queue: [], queueIndex: 0,
      playing: false, visible: false, minimized: false,
      hasNext: false, hasPrev: false,
      playTrack: () => {}, playQueue: () => {}, pause: () => {}, resume: () => {},
      toggle: () => {}, minimize: () => {}, expand: () => {}, close: () => {},
      next: () => {}, prev: () => {},
    };
  }
  return ctx;
}
